import apiClient from "./baseService";
import {handleAxiosError} from "./axiosErrorHandler";
import {Field, FieldPostBody, FieldGetBody} from "../types/domain/field";
import {Attachment} from "../types/domain/attachment";
import {toCropCycleClient} from "../utils/cropCycleUtils";
import {PaginatedResponse} from "../types/PaginatedResponse";

export const createField = async (companyId: string, body: FieldPostBody): Promise<Field> => {
    return await apiClient
        .post<Field>(`/companies/${companyId}/fields`, body)
        .then((response) => response.data)
        .catch(handleAxiosError);
};

export const editField = async (companyId: string, id: string, body: FieldPostBody): Promise<Field> => {
    return await apiClient
        .patch<Field>(`/companies/${companyId}/fields/${id}`, body)
        .then((response) => response.data)
        .catch(handleAxiosError);
};

export interface AttachmentsQuery {
    fieldId?: string;
    farmId?: string;
}
export const getAttachments = async (companyId: string, query: AttachmentsQuery): Promise<Attachment[]> => {
    let params = {};
    if (query) {
        params = query;
    }
    return await apiClient
        .get<Attachment[]>(`/companies/${companyId}/attachments`, {params})
        .then((response) => response.data)
        .catch(handleAxiosError);
};
export const getAttachmentById = async (companyId: string, id: string): Promise<Blob> => {
    return await apiClient
        .get<Blob>(`/companies/${companyId}/attachments/${id}`, {responseType: "blob"})
        .then((response) => {
            return response.data;
        })
        .catch(handleAxiosError);
};
export const getAttachmentsArchive = async (companyId: string, query: AttachmentsQuery): Promise<Blob> => {
    let params = {};
    if (query) {
        params = query;
    }
    return await apiClient
        .get<Blob>(`/companies/${companyId}/attachments/`, {
            params: {...params, archive: true},
            responseType: "blob",
        })
        .then((response) => response.data)
        .catch(handleAxiosError);
};
export const uploadAttachments = async (
    companyId: string,
    query: AttachmentsQuery,
    formData: FormData,
): Promise<string> => {
    let params = {};
    if (query) {
        params = query;
    }
    return await apiClient
        .post<string>(`/companies/${companyId}/attachments`, formData, {
            params,
            headers: {
                "Content-Type": "multipart/form-data",
            },
        })
        .then((response) => response.data)
        .catch(handleAxiosError);
};

export const deleteField = async (companyId: string, id: string): Promise<string> => {
    return await apiClient
        .delete<string>(`/companies/${companyId}/fields/${id}`)
        .then((response) => response.data)
        .catch(handleAxiosError);
};

export const finalizeField = async (companyId: string, id: string): Promise<string> => {
    return await apiClient
        .post<string>(`/companies/${companyId}/fields/${id}/finalize`)
        .then((response) => response.data)
        .catch(handleAxiosError);
};
interface IGetAllFieldsParams {
    companyId: string;
    orderId?: string;
    farmId?: string;
    cropId?: string;
    onlyActiveOrders?: boolean;
    name?: string;
    attributes?: string[];
}

const sortFields = (a: Field, b: Field): number => {
    return a.name < b.name ? -1 : a.name > b.name ? 1 : 0;
};

export const getAllFields = async ({
    companyId,
    orderId,
    farmId,
    cropId,
    onlyActiveOrders,
    name,
    attributes,
}: IGetAllFieldsParams): Promise<Field[]> => {
    return apiClient
        .get<PaginatedResponse<FieldGetBody>>(`/companies/${companyId}/fields`, {
            params: {orderId, farmId, cropId, onlyActiveOrders, name, attributes},
        })
        .then((response) => {
            if (!attributes || attributes?.includes("cropCycles")) {
                let items: FieldGetBody[] = [];
                items = response.data.data;
                const result = items.map((field) => ({
                    ...field,
                    cropCycles: field.cropCycles.map(toCropCycleClient),
                }));
                return result.sort(sortFields);
            }
            return response.data.data as unknown as Field[];
        })
        .catch(handleAxiosError);
};

export const getCropIds = async (companyId: string): Promise<string[]> => {
    return await apiClient
        .get<string[]>(`/companies/${companyId}/fields/cropTypes`)
        .then((response) => response.data)
        .catch(handleAxiosError);
};
